import { Lock, RotateCcw, CheckCircle2, XCircle } from 'lucide-react'

export interface ClosedConversationBarProps {
  isReopening: boolean
  onReopen: () => void
  closeReason?: string | null
  closeNotes?: string | null
  closedAt?: string | null
}

const REASON_METADATA: Record<string, { label: string; isWon: boolean }> = {
  SALE_CONFIRMED: { label: 'Pedido Confirmado', isWon: true },
  PAYMENT_RECEIVED: { label: 'Pago Verificado', isWon: true },
  ORDER_DELIVERED: { label: 'Pedido Entregado', isWon: true },
  ABANDONED_CART: { label: 'Carrito Abandonado', isWon: false },
  OUT_OF_COVERAGE: { label: 'Fuera de Cobertura', isWon: false },
  OUT_OF_STOCK: { label: 'Sin Stock', isWon: false },
  PRICE_OBJECTION: { label: 'Objeción de Precio', isWon: false },
  CANCELLED_BY_CLIENT: { label: 'Cancelado por el Cliente', isWon: false },
  CUSTOMER_SUPPORT: { label: 'Atención / Soporte', isWon: false },
  SPAM_OR_WRONG_NUMBER: { label: 'Spam o Número Erróneo', isWon: false },
}

export function ClosedConversationBar({
  isReopening,
  onReopen,
  closeReason,
  closeNotes,
  closedAt,
}: ClosedConversationBarProps) {
  const reasonMeta = closeReason ? REASON_METADATA[closeReason] : undefined
  const isWon = reasonMeta?.isWon ?? false
  const reasonLabel = reasonMeta?.label || (closeReason ? closeReason.replace(/_/g, ' ') : null)

  const formattedClosedAt = closedAt
    ? new Date(closedAt).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <div
      className={`border-t px-4 py-3.5 lg:px-6 transition-colors ${
        isWon ? 'border-emerald-200 bg-emerald-50/80' : 'border-rose-100 bg-rose-50/70'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 lg:mx-auto lg:max-w-5xl">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${
              isWon ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}
          >
            {isWon ? <CheckCircle2 className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`text-xs font-bold sm:text-sm ${isWon ? 'text-emerald-950' : 'text-rose-950'}`}>
                Conversación cerrada
              </p>
              {reasonLabel && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                    isWon
                      ? 'bg-emerald-100/80 text-emerald-800 border-emerald-300'
                      : 'bg-rose-100/80 text-rose-800 border-rose-300'
                  }`}
                >
                  {isWon ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  ) : (
                    <XCircle className="w-3 h-3 text-rose-500" />
                  )}
                  {reasonLabel}
                </span>
              )}
              {formattedClosedAt && (
                <span className="text-[11px] text-slate-500">({formattedClosedAt})</span>
              )}
            </div>
            {closeNotes ? (
              <p className="text-[11px] sm:text-xs text-slate-600 truncate mt-0.5 max-w-xl">
                <span className="font-semibold text-slate-700">Nota:</span> {closeNotes}
              </p>
            ) : (
              <p className={`text-[11px] sm:text-xs ${isWon ? 'text-emerald-800/80' : 'text-rose-800/80'}`}>
                Esta conversación está finalizada. Reábrela para reanudar la comunicación con el cliente.
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onReopen}
          disabled={isReopening}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-emerald-700 disabled:opacity-50 active:scale-[0.98] ml-auto flex-shrink-0"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          <span>{isReopening ? 'Reabriendo...' : 'Reabrir conversación'}</span>
        </button>
      </div>
    </div>
  )
}
